'use client';

import { useCallback, useEffect, useRef } from 'react';
import usePath from 'helpers/hooks/usePath';
import useRefreshImages from '../hooks/useRefreshImages';

const INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const DONT_VALIDATE_IMAGES_ON_PATH_PREFIXES = ['/designer', '/cart', '/checkout', '/account', '/order-details'];

const ImageRefresher = () => {
  const { refreshImagesForCarts, refreshImagesForSession, miniCartsLoaded } = useRefreshImages();

  // Put the path in a ref so that the interval callback always has the latest value without
  // causing a rebuild of the interval callback
  const { pathWithoutQuery } = usePath();

  /*
   * Keep the current path available to interval/event callbacks
   * without rebuilding those listeners when the route changes.
   */
  const pathWithoutQueryRef = useRef(pathWithoutQuery);
  useEffect(() => {
    pathWithoutQueryRef.current = pathWithoutQuery;
  }, [pathWithoutQuery]);

  /*
   * Keep the latest refresh images callbacks in a ref.
   *
   * For example, refreshImagesForCarts can change when miniCarts changes. Using a ref
   * prevents the interval and browser listeners from being rebuilt
   * for every mini cart cache update.
   */
  const refreshImagesForCartsRef = useRef(refreshImagesForCarts);
  const refreshImagesForSessionRef = useRef(refreshImagesForSession);

  useEffect(() => {
    refreshImagesForCartsRef.current = refreshImagesForCarts;
    refreshImagesForSessionRef.current = refreshImagesForSession;
  }, [refreshImagesForCarts, refreshImagesForSession]);

  const validateImagesAndLogErrors = useCallback(async () => {
    try {
      await refreshImagesForCartsRef.current();
    } catch (error) {
      console.error('[Image Refresher] Error validating and reauthorizing cart images.', error);
    }
    try {
      await refreshImagesForSessionRef.current();
    } catch (error) {
      console.error('[Image Refresher] Error refreshing session images.', error);
    }
  }, []);

  /*
   * Prevent repeated initial validation caused by unrelated renders.
   */
  const hasRunInitialValidationRef = useRef(false);

  /*
   * Run initial validation only after mini carts are available.
   */
  useEffect(() => {
    if (!miniCartsLoaded) {
      return;
    }

    if (hasRunInitialValidationRef.current) {
      return;
    }

    hasRunInitialValidationRef.current = true;

    console.info('[Image Refresher] Running initial validation for images');
    void validateImagesAndLogErrors();
  }, [miniCartsLoaded, validateImagesAndLogErrors]);

  /*
   * Set up long-lived interval and browser event listeners.
   */
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      // Don't validate on interval on these pages to fix possible conflicts with cart updates.
      // We still are validating on focus and visibility change below, and on first load.
      // Plus some pages call validateImages directly (such as the cart right before checkout)
      const currentPathWithoutQuery = pathWithoutQueryRef.current;
      const dontValidateImages = DONT_VALIDATE_IMAGES_ON_PATH_PREFIXES.some(
        (prefix) =>
          currentPathWithoutQuery === prefix || currentPathWithoutQuery.startsWith(`${prefix}/`),
      );
      if (!dontValidateImages) {
        console.info('[Image Refresher] Validating images on interval');
        void validateImagesAndLogErrors();
      }
    }, INTERVAL_MS);

    // Catch up after background/suspend
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.info('[Image Refresher] Validating images on visibility change');
        void validateImagesAndLogErrors();
      }
    };

    const handleFocus = () => {
      console.info('[Image Refresher] Validating images on focus');
      void validateImagesAndLogErrors();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [validateImagesAndLogErrors]);

  return null;
};

export default ImageRefresher;
